import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { TopNav } from "@/components/TopNav";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { RankBadge } from "@/components/RankBadge";
import { AvatarUploader } from "@/components/AvatarUploader";
import { getRank, nextRank, progressToNext } from "@/lib/ranks";
import { Edit2, Save, X, Phone, Image as ImageIcon, Target, Coins } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { id } = useParams();
  const { user: me, refresh } = useAuth();
  const [player, setPlayer] = useState(null);
  const [history, setHistory] = useState([]);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ nickname: "", phone: "", avatar_url: "" });

  const playerId = id || me?.id;
  const isOwn = !id || id === me?.id;

  useEffect(() => {
    if (!playerId) return;
    api.get(`/players/${playerId}`).then(({ data }) => {
      setPlayer(data);
      setForm({ nickname: data.nickname, phone: data.phone || "", avatar_url: data.avatar_url || "" });
    });
    api.get(`/matches?player_id=${playerId}&limit=20`).then(({ data }) => setHistory(data));
  }, [playerId]);

  const save = async () => {
    try {
      const updates = { phone: form.phone, avatar_url: form.avatar_url };
      await api.patch("/players/me", updates);
      // If nickname changed, send as approval request
      if (form.nickname && form.nickname !== player.nickname) {
        await api.post("/me/nickname-request", { nickname: form.nickname });
        toast.success("Nickname đã gửi chờ admin duyệt");
      } else {
        toast.success("Đã cập nhật hồ sơ");
      }
      const { data } = await api.get(`/players/${playerId}`);
      setPlayer(data);
      setEdit(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    }
  };

  if (!player) return <div className="min-h-screen bg-[#05050a]"><TopNav /><div className="p-10 text-white/40">Đang tải...</div></div>;

  const rank = getRank(player.elo);
  const nxt = nextRank(player.elo);
  const prog = progressToNext(player.elo);

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="relative rounded-2xl bg-[#0c0d14] border border-white/8 p-6 sm:p-8 overflow-hidden mb-6">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl" style={{ background: `${rank.color}22` }} />
          <div className="relative grid sm:grid-cols-[auto_1fr] gap-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-2xl overflow-hidden border-2" style={{ borderColor: rank.color }}>
                <img src={player.avatar_url} alt="" className="w-full h-full object-cover" />
              </div>
              {isOwn && (
                <div className="mt-2">
                  <AvatarUploader onUploaded={(u) => { setPlayer(u); refresh(); }} className="w-full justify-center" />
                  {player.avatar_approved === false && <div className="mt-1.5 text-[10px] text-yellow-400 text-center">Avatar đang chờ duyệt</div>}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-widest mb-1">@{player.username}</div>
                  {edit ? (
                    <input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                      className="bg-white/5 border border-white/15 rounded px-3 py-1.5 text-2xl font-display font-bold mb-2 outline-none focus:border-cyan-400/60" />
                  ) : (
                    <div className="font-display text-3xl sm:text-4xl font-bold mb-2">{player.nickname}</div>
                  )}
                  <RankBadge elo={player.elo} size="lg" />
                </div>
                {isOwn && (
                  <button
                    data-testid="edit-profile-btn"
                    onClick={() => (edit ? save() : setEdit(true))}
                    className="px-3 py-2 rounded-md text-sm border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 flex items-center gap-2"
                  >
                    {edit ? <><Save size={14} /> Lưu</> : <><Edit2 size={14} /> Sửa</>}
                  </button>
                )}
              </div>

              {edit && (
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-white/50 flex items-center gap-1 mb-1"><Phone size={10} /> SĐT</label>
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-white/50 flex items-center gap-1 mb-1"><ImageIcon size={10} /> Avatar URL</label>
                    <input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
                  </div>
                  <button onClick={() => setEdit(false)} className="sm:col-span-2 text-xs text-white/50 hover:text-white/80 flex items-center gap-1 justify-end"><X size={12} /> Hủy</button>
                </div>
              )}

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ["ELO", player.elo],
                  ["Trận", player.total_matches],
                  ["Thắng", player.wins],
                  ["Win rate", `${player.win_rate}%`],
                  ["Coin", player.coin || 0, "text-yellow-300"],
                  ["Điểm bắn đền", player.total_break_points || 0, "text-orange-300"],
                  ["Coin từ bắn đền", player.total_break_coin || 0, "text-yellow-300"],
                  ["Chuỗi tốt nhất", player.best_streak || 0, "text-red-300"],
                ].map(([l, v, color]) => (
                  <div key={l} className="rounded-md bg-white/[0.03] border border-white/8 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-white/40">{l}</div>
                    <div className={`font-display font-bold text-xl ${color || ""}`}>{v}</div>
                  </div>
                ))}
              </div>

              {nxt.name !== rank.name && (
                <div className="mt-5">
                  <div className="flex justify-between text-xs text-white/50 mb-1.5">
                    <span>{rank.name}</span><span>{nxt.name} · còn {nxt.min - player.elo} ELO</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${prog}%`, background: `linear-gradient(90deg, ${rank.color}, ${nxt.color})` }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-5">
          <h2 className="font-display font-semibold mb-4">Lịch sử trận đấu</h2>
          {history.length === 0 ? (
            <div className="text-white/40 py-10 text-center text-sm">Chưa có trận nào</div>
          ) : (
            <div className="space-y-2">
              {history.map((m) => {
                const isA = m.player_a_id === player.id;
                const myDelta = isA ? m.elo_delta_a : m.elo_delta_b;
                const opp = isA ? m.player_b_nickname : m.player_a_nickname;
                const won = m.winner_id === player.id;
                return (
                  <div key={m.id} className="flex items-center gap-3 p-3 rounded-md bg-white/[0.02] border border-white/5">
                    <div className={`w-1 h-10 rounded-full ${won ? "bg-cyan-400" : m.status === "playing" ? "bg-yellow-400" : "bg-red-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">vs {opp}</div>
                      <div className="text-xs text-white/50">Bàn {m.table_number} · BO{m.best_of} · {m.mode}</div>
                    </div>
                    <div className="text-sm font-mono-num">{isA ? m.score_a : m.score_b}-{isA ? m.score_b : m.score_a}</div>
                    <div className={`text-sm font-mono-num font-semibold ${myDelta >= 0 ? "text-cyan-300" : "text-red-400"}`}>
                      {myDelta >= 0 ? "+" : ""}{myDelta}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
